<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">{{ $t("To'lov turlari") }}</h2>
        <div class="header-actions">
          <div class="search-wrapper">
            <b-icon icon="search" class="search-icon"></b-icon>
            <input v-model="filter" type="search" :placeholder="$t('Qidiruv...')" class="modern-search">
          </div>
          <button v-if="dirty" class="save-order-btn" :disabled="savingOrder" @click="saveOrder">
            <b-icon :icon="savingOrder ? 'arrow-repeat' : 'check2-circle'"></b-icon>
            {{ savingOrder ? '...' : $t('Tartibni saqlash') }}
          </button>
          <button class="refresh-btn" @click="fetchList" title="Yangilash">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
          <button class="add-btn" @click="openCreate">
            <b-icon icon="plus-circle-fill"></b-icon> {{ $t("Qo'shish") }}
          </button>
        </div>
      </div>

      <!-- JADVAL -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width: 46px"></th>
                <th style="width: 80px">ID</th>
                <th>{{ $t("Nomi") }}</th>
                <th style="width: 140px; text-align: center;">{{ $t("Holati") }}</th>
                <th style="width: 140px; text-align: center;">{{ $t("Amallar") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in filteredItems"
                :key="item.id"
                :draggable="canReorder"
                :class="{ 'drag-row': canReorder, 'dragging': dragIndex === index }"
                @dragstart="onDragStart(index, $event)"
                @dragover.prevent="onDragOver(index)"
                @drop.prevent="onDrop"
                @dragend="onDragEnd"
              >
                <td class="drag-cell">
                  <span v-if="canReorder" class="drag-handle" :title="$t('Sudrab tartiblang')">
                    <b-icon icon="list"></b-icon>
                  </span>
                  <span v-else class="drag-handle disabled" :title="$t('Qidiruvni tozalang')">
                    <b-icon icon="list"></b-icon>
                  </span>
                </td>
                <td><span class="id-badge">#{{ item.id }}</span></td>
                <td class="font-bold">{{ item.name }}</td>
                <td class="text-center">
                  <span v-if="isProtected(item.id)" class="badge-system">{{ $t("Tizim") }}</span>
                  <span v-else class="badge-custom">{{ $t("Qo'shilgan") }}</span>
                </td>
                <td>
                  <div class="actions">
                    <button class="icon-btn edit" :disabled="isProtected(item.id)" @click="openEdit(item)">
                      <b-icon icon="pencil"></b-icon>
                    </button>
                    <button class="icon-btn del" :disabled="isProtected(item.id)" @click="confirmDelete(item)">
                      <b-icon icon="trash"></b-icon>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredItems.length === 0">
                <td colspan="5" class="empty-row text-center">{{ $t("Ma'lumot topilmadi") }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table-footer">
          <div class="info">{{ $t("Jami") }}: <b>{{ filteredItems.length }}</b></div>
          <div class="reorder-hint">
            <span v-if="!canReorder">🔒 {{ $t("Tartiblash uchun qidiruvni tozalang") }}</span>
            <span v-else-if="dirty" class="dirty">✏️ {{ $t("Tartib o'zgardi — saqlashni unutmang") }}</span>
            <span v-else>↕️ {{ $t("Qatorlarni sudrab tartiblang") }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- CREATE/EDIT MODAL -->
    <b-modal v-model="showModal" :title="form.id ? $t('Tahrirlash') : $t('Yangi to\'lov turi')" hide-footer centered>
      <div class="mb-3">
        <label class="form-label fw-semibold">{{ $t("Nomi") }} <span class="text-danger">*</span></label>
        <input
          ref="nameInput"
          v-model="form.name"
          type="text"
          class="form-control form-control-lg"
          maxlength="20"
          :placeholder="$t('Masalan: Click, Payme')"
          @keyup.enter="save"
        >
      </div>
      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-light" @click="showModal = false">{{ $t('Bekor') }}</button>
        <button class="btn btn-primary px-4" :disabled="saving" @click="save">
          <span v-if="saving"><span class="spinner-border spinner-border-sm"></span> ...</span>
          <span v-else>{{ $t("Saqlash") }}</span>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PayTypeIndex',
  data() {
    return {
      items: [],
      filter: '',
      showModal: false,
      saving: false,
      form: { id: null, name: '' },
      protectedIds: [1, 2, 3, 4],
      // Drag-reorder holati
      dragIndex: null,
      dirty: false,
      savingOrder: false,
    };
  },
  computed: {
    filteredItems() {
      const q = (this.filter || '').toLowerCase().trim();
      if (!q) return this.items;
      return this.items.filter(i => (i.name || '').toLowerCase().includes(q));
    },
    // Tartiblash faqat qidiruvsiz (to'liq ro'yxat ko'rinib turganda) ishlaydi
    canReorder() {
      return !(this.filter || '').trim();
    }
  },
  mounted() { this.fetchList(); },
  methods: {
    isProtected(id) { return this.protectedIds.includes(Number(id)); },
    async fetchList() {
      const res = await axios.get('api/v1/pay-type/');
      this.items = res.data || [];
      this.dirty = false;
    },

    // ──────── DRAG-REORDER ────────
    onDragStart(index, e) {
      if (!this.canReorder) return;
      this.dragIndex = index;
      if (e && e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox uchun drag boshlanishiga ruxsat
        try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
      }
    },
    onDragOver(index) {
      if (this.dragIndex === null || this.dragIndex === index) return;
      // Jonli qayta tartiblash: sudralayotgan elementni yangi joyga ko'chiramiz
      const moved = this.items.splice(this.dragIndex, 1)[0];
      this.items.splice(index, 0, moved);
      this.dragIndex = index;
      this.dirty = true;
    },
    onDrop() { this.dragIndex = null; },
    onDragEnd() { this.dragIndex = null; },

    async saveOrder() {
      this.savingOrder = true;
      try {
        const items = this.items.map((it, idx) => ({ id: it.id, order_index: idx + 1 }));
        const res = await axios.post('api/v1/pay-type/reorder', { items });
        this.items = res.data || this.items;
        this.dirty = false;
      } catch (e) {
        alert(this.$t("Xatolik") + ": " + (e.response?.data?.message || e.message));
      } finally {
        this.savingOrder = false;
      }
    },
    openCreate() {
      this.form = { id: null, name: '' };
      this.showModal = true;
      this.$nextTick(() => setTimeout(() => this.$refs.nameInput && this.$refs.nameInput.focus(), 200));
    },
    openEdit(item) {
      this.form = { id: item.id, name: item.name };
      this.showModal = true;
      this.$nextTick(() => setTimeout(() => this.$refs.nameInput && this.$refs.nameInput.focus(), 200));
    },
    async save() {
      const name = (this.form.name || '').trim();
      if (!name) return alert(this.$t("Nomni kiriting"));
      this.saving = true;
      try {
        if (this.form.id) {
          await axios.patch(`api/v1/pay-type/id/${this.form.id}`, { name });
        } else {
          await axios.post('api/v1/pay-type/', { name });
        }
        this.showModal = false;
        this.fetchList();
      } catch (e) {
        alert(this.$t("Xatolik") + ": " + (e.response?.data?.message || e.message));
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(item) {
      if (!confirm(`${item.name} — ${this.$t("o'chirishni tasdiqlang")}?`)) return;
      try {
        await axios.delete(`api/v1/pay-type/id/${item.id}`);
        this.fetchList();
      } catch (e) {
        alert(this.$t("Xatolik") + ": " + (e.response?.data?.message || e.message));
      }
    }
  }
};
</script>

<style scoped>
.g-page { padding: 20px; background: #f8fafc; height: 100%; display: flex; flex-direction: column; min-height: 0; }
.content-container { max-width: 1000px; width: 100%; margin: 0 auto; display: flex; flex-direction: column; height: 100%; min-height: 0; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
.page-title { color: #a142f4; font-weight: 800; font-size: 26px; margin: 0; }
.header-actions { display: flex; align-items: center; gap: 10px; }

.search-wrapper { position: relative; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.modern-search { background: white; border: 1px solid #e2e8f0; padding: 8px 15px 8px 35px; border-radius: 12px; width: 240px; outline: none; }
.modern-search:focus { border-color: #a142f4; box-shadow: 0 0 0 4px rgba(161,66,244,.1); }

.refresh-btn, .add-btn { border: none; height: 40px; border-radius: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 8px; padding: 0 16px; transition: .2s; }
.refresh-btn { background: #f1f5f9; color: #475569; width: 40px; justify-content: center; padding: 0; }
.refresh-btn:hover { background: #e2e8f0; }
.add-btn { background: linear-gradient(135deg, #a142f4 0%, #8b2cf5 100%); color: white; box-shadow: 0 4px 15px rgba(161,66,244,.3); padding: 0 20px; }
.add-btn:hover { transform: translateY(-1px); }

.table-card { background: white; border-radius: 20px; padding: 10px; box-shadow: 0 10px 30px rgba(0,0,0,.03); border: 1px solid #f1f5f9; flex: 1; display: flex; flex-direction: column; min-height: 0; }
.table-wrapper { overflow-y: auto; flex: 1; min-height: 0; }
.custom-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.custom-table thead { position: sticky; top: 0; z-index: 2; }
.custom-table th { background: white; padding: 14px 15px; color: #94a3b8; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid #f1f5f9; text-align: left; }
.custom-table td { padding: 12px 15px; border-bottom: 1px solid #f8fafc; font-size: 14px; color: #334155; vertical-align: middle; }
.custom-table tbody tr:hover { background: #faf8ff; }
.font-bold { font-weight: 700; color: #1e293b; }
.id-badge { background: #eff6ff; color: #2563eb; padding: 3px 10px; border-radius: 8px; font-weight: 700; font-size: 12px; }

.badge-system { background: #fef3c7; color: #d97706; padding: 4px 10px; border-radius: 8px; font-size: 12px; font-weight: 700; }
.badge-custom { background: #dcfce7; color: #16a34a; padding: 4px 10px; border-radius: 8px; font-size: 12px; font-weight: 700; }

.actions { display: flex; gap: 8px; justify-content: center; }
.icon-btn { width: 34px; height: 34px; border-radius: 10px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: .2s; }
.icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.edit { background: #eff6ff; color: #2563eb; }
.edit:hover:not(:disabled) { background: #2563eb; color: white; }
.del  { background: #fff1f2; color: #e11d48; }
.del:hover:not(:disabled)  { background: #e11d48; color: white; }

.table-footer { padding: 12px 15px; border-top: 1px solid #f1f5f9; color: #64748b; font-size: 13px; display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.reorder-hint { font-size: 12px; color: #94a3b8; }
.reorder-hint .dirty { color: #d97706; font-weight: 700; }
.empty-row { text-align: center; padding: 50px 0 !important; color: #94a3b8; font-style: italic; }

/* Save-order tugmasi */
.save-order-btn {
  border: none; height: 40px; border-radius: 12px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 8px; padding: 0 18px; transition: .2s;
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); color: white;
  box-shadow: 0 4px 15px rgba(22,163,74,.3);
}
.save-order-btn:hover:not(:disabled) { transform: translateY(-1px); }
.save-order-btn:disabled { opacity: .6; cursor: not-allowed; }

/* Drag-reorder */
.drag-cell { text-align: center; }
.drag-handle { color: #cbd5e1; cursor: grab; font-size: 16px; display: inline-flex; }
.drag-handle:hover { color: #a142f4; }
.drag-handle.disabled { cursor: not-allowed; opacity: .4; }
.drag-row { cursor: grab; }
.drag-row.dragging { opacity: .45; background: #faf5ff !important; }
.drag-row.dragging .drag-handle { cursor: grabbing; }
</style>
